"use client";

import { useEffect, useRef } from "react";
import { track } from "@/lib/track";

/**
 * An invisible line at the end of a post's article text, just above its
 * FAQ when it has one. When a reader scrolls it into view, one article_end
 * event records the post and the seconds since the page opened. Scroll depth
 * percentages count the FAQs, sources and footer too, so they can't say
 * whether someone finished the article; this can.
 */
export default function ArticleEndMarker({ slug, beforeFaq }: { slug: string; beforeFaq: boolean }) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = ref.current;
    if (!el || typeof IntersectionObserver === "undefined") return;
    const start = Date.now();
    let done = false;
    const io = new IntersectionObserver((entries) => {
      // Only a reader who scrolled counts, not a page opened at an anchor.
      if (done || !entries.some((e) => e.isIntersecting) || window.scrollY <= 0) return;
      done = true;
      track({
        event: "article_end",
        source: "blog",
        meta: { path: window.location.pathname, slug, seconds: Math.round((Date.now() - start) / 1000), before_faq: beforeFaq },
      });
      io.disconnect();
    });
    io.observe(el);
    return () => io.disconnect();
  }, [slug, beforeFaq]);
  return <div ref={ref} aria-hidden="true" style={{ height: 1 }} />;
}
